/**
 * Composer-Paragraphen-Suche: §-Parse + Labels für Lookup/Heften.
 * Kein LEXCORE-Korpus im Browser — nur UI-Erkennung; Nachschlagen über rechtsfrage.
 */

export interface ParagraphRef {
  /** z. B. "16" oder "16 Abs. 1" */
  section: string;
  /** z. B. "MRG", "ABGB" — optional wenn nur Zahl */
  law?: string;
  /** Kanonisches Label „§ 16 MRG“ */
  label: string;
  /** Stabiler Key für Listen */
  key: string;
}

/** Häufige AT-Kurzformen für Typeahead (kein Korpus). */
export const PARAGRAPH_LAW_HINTS = [
  'MRG',
  'ABGB',
  'WEG',
  'KSchG',
  'ZPO',
  'EO',
  'StGB',
  'RAO',
  'UGB',
  'DSG',
  'DSGVO',
] as const;

const LAW_ALT = PARAGRAPH_LAW_HINTS.join('|');

/** „§ 16 MRG“, „16 MRG“, „§16“, „MRG § 16“, „§§ 16, 29 MRG“ */
const REF_RE = new RegExp(
  String.raw`(?:§{1,2}\s*)?(\d{1,4}(?:\s*(?:Abs\.?|Absatz)\s*\d{1,2})?)\s*(?:\/\s*\d{1,2})?\s*(${LAW_ALT})\b` +
    String.raw`|(${LAW_ALT})\s*§{0,2}\s*(\d{1,4}(?:\s*(?:Abs\.?|Absatz)\s*\d{1,2})?)` +
    String.raw`|§{1,2}\s*(\d{1,4}(?:\s*(?:Abs\.?|Absatz)\s*\d{1,2})?)(?!\s*(?:Abs\.?|Absatz))`,
  'gi',
);

function normalizeSection(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

function makeRef(section: string, law?: string): ParagraphRef {
  const sec = normalizeSection(section);
  const lawUp = law?.trim().toUpperCase() || undefined;
  const label = lawUp ? `§ ${sec} ${lawUp}` : `§ ${sec}`;
  return {
    section: sec,
    law: lawUp,
    label,
    key: `${lawUp ?? '§'}:${sec}`.toLowerCase(),
  };
}

/** Extrahiert alle erkannten §-Refs aus freiem Tippen. */
export function parseParagraphQuery(query: string): ParagraphRef[] {
  const text = query.trim();
  if (!text) return [];

  const found = new Map<string, ParagraphRef>();
  const re = new RegExp(REF_RE.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    let ref: ParagraphRef | null = null;
    if (m[1] && m[2]) ref = makeRef(m[1], m[2]);
    else if (m[3] && m[4]) ref = makeRef(m[4], m[3]);
    else if (m[5]) ref = makeRef(m[5]);
    if (ref) found.set(ref.key, ref);
  }

  // Fallback: „16 MRG“ ohne § (Regex deckt das ab) — oder nur Zahl + Gesetz getrennt
  if (found.size === 0) {
    const loose = text.match(
      new RegExp(String.raw`^§?\s*(\d{1,4})\s*(${LAW_ALT})?\s*$`, 'i'),
    );
    if (loose?.[1]) {
      const ref = makeRef(loose[1], loose[2]);
      found.set(ref.key, ref);
    }
  }

  // „§§ 16, 29 MRG“: Gesetz am Ende auf bare §-Nummern übertragen
  const trailingLaw = text.match(new RegExp(String.raw`\b(${LAW_ALT})\s*$`, 'i'));
  if (trailingLaw) {
    const law = trailingLaw[1].toUpperCase();
    const bareNums = text.matchAll(/§{0,2}\s*(\d{1,4})/gi);
    for (const n of bareNums) {
      const ref = makeRef(n[1], law);
      found.set(ref.key, ref);
    }
    for (const [key, ref] of [...found.entries()]) {
      if (!ref.law) {
        found.delete(key);
        const fixed = makeRef(ref.section, law);
        found.set(fixed.key, fixed);
      }
    }
  }

  return Array.from(found.values());
}

/** Typeahead-Vorschläge aus Query (lokal, ohne API). */
export function suggestParagraphRefs(query: string, limit = 6): ParagraphRef[] {
  const parsed = parseParagraphQuery(query);
  if (parsed.length > 0) return parsed.slice(0, limit);

  const q = query.trim();
  if (!q) {
    return PARAGRAPH_LAW_HINTS.slice(0, 4).map((law) => makeRef('…', law));
  }

  const numOnly = q.match(/^§?\s*(\d{1,4})\s*$/);
  if (numOnly?.[1]) {
    return PARAGRAPH_LAW_HINTS.slice(0, limit).map((law) => makeRef(numOnly[1], law));
  }

  const lawOnly = q.match(new RegExp(String.raw`^(${LAW_ALT})\s*$`, 'i'));
  if (lawOnly?.[1]) {
    const law = lawOnly[1].toUpperCase();
    return [16, 29, 1, 12].map((n) => makeRef(String(n), law)).slice(0, limit);
  }

  return [];
}

/** Lookup-Prompt: Nachschlagen ohne Fallanalyse. */
export function buildParagraphLookupPrompt(refs: ParagraphRef[]): string {
  const labels = refs.map((r) => r.label).join(', ');
  return (
    `Bitte nur nachschlagen: ${labels}. ` +
    'Kurzer Normwortlaut bzw. Überblick aus LEXCORE — keine Fallanalyse und kein Sachverhalt.'
  );
}

/** Kontextblock für angeheftete §§ (Composer / Webhook-Text). */
export function buildAttachedParagraphContext(refs: ParagraphRef[]): string {
  if (!refs.length) return '';
  const labels = refs.map((r) => r.label).join('; ');
  return `Angeheftete Paragraphen (LEXCORE-Kontext): ${labels}\n\n`;
}

export function mergeParagraphRefs(
  existing: ParagraphRef[],
  incoming: ParagraphRef[],
): ParagraphRef[] {
  const map = new Map(existing.map((r) => [r.key, r]));
  for (const r of incoming) map.set(r.key, r);
  return Array.from(map.values());
}
